import React from 'react';
import { Check } from 'lucide-react';

const productDescriptions: Record<string, React.ReactNode> = {
  'launch-guide': (
    <div className="space-y-6">
      <div className="space-y-3">
        <h2 className="text-2xl font-bold">The Ultimate Step-by-Step Guide to AI-Powered App Development</h2>
        <p className="text-lg font-medium text-indigo-600">Everything You Need To Launch a Successful App with AI</p>
        <p className="text-sm text-gray-700 leading-relaxed">
          Turn Your Idea into an AI-Powered App – Follow a proven roadmap to build, launch, and fund your AI-driven app.
        </p>
      </div>
      
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">What You'll Learn:</h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">AI-Powered Market Research – Validate your idea fast using AI tools to analyze trends and user demand.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">Fully Build & Launch Your AI Mobile App in 30 Days or Less – Step-by-step guidance to develop and deploy your AI-powered app fast.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">Stand Out with AI Innovation – Use AI to outperform competitors and deliver a smarter user experience.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">Monetize with AI Strategies – Unlock high-profit revenue models powered by AI automation and analytics.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">Secure Funding with AI Insights – Leverage AI-driven data to craft a pitch investors can't ignore.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">AI-Driven Growth Hacks – Automate marketing, optimize ads, and acquire users at scale.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">Scale Faster with AI Automation – Cut costs and accelerate growth using AI-powered efficiency.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">Avoid Costly Startup Mistakes – Learn industry secrets to save time, money, and headaches.</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="bg-green-100 rounded-full p-1 flex-shrink-0 mt-1">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-gray-700">Exclusive AI Tools & Founder Network – Get investor-ready templates, AI resources, and expert connections.</span>
          </li>
        </ul>
      </div>
    </div>
  ),
  'ai-prompts': (
    <div className="space-y-3">
      <p className="text-sm text-gray-700">Use this complete AI App Development copy and paste swipe file to tell AI exactly how to design, code, and publish it in the app stores. Plus, 100 full-proof AI prompts to make it completely custom!</p>
      <p className="text-sm text-gray-700 font-semibold">This information will save you months of time and tens of thousands of dollars in development fees!</p>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm">AI Development Copy and Paste Swipe File</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm">100 Full-Proof AI Prompts to build the perfect app</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm">Case study: How I Built an AI-Powered App in 24 Hours</span>
        </li>
      </ul>
    </div>
  ),
  'fast-track': (
    <div className="space-y-3 text-left">
      <p className="text-sm text-gray-700 font-medium text-left">Build Your First AI App in a Weekend</p>
      <p className="text-sm text-gray-700 text-left">Get ready to turn your app idea into reality—fast! This step-by-step course is designed to help you build and launch your first AI-powered app in just one weekend. Whether you're a complete beginner or an entrepreneur with a vision, this hands-on program will guide you through the entire process, from concept to deployment.</p>
      <p className="font-medium text-sm text-left">What You'll Learn:</p>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">AI Basics Simplified – Understand how AI enhances apps and learn the essential tools needed to get started.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">No-Code & Low-Code Solutions – Discover how to build AI-powered apps without extensive programming knowledge.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Step-by-Step Development – Follow a structured approach to designing, building, and testing your app.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Practical Hands-On Exercises – Apply what you learn in real-time, ensuring you make tangible progress.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Launch & Monetization Strategies – Learn how to deploy your app and explore ways to generate revenue.</span>
        </li>
      </ul>
      <p className="font-medium text-sm mt-3 text-left">Why Take This Course?</p>
      <ul className="space-y-1">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">No Technical Experience Needed – Designed for entrepreneurs, creators, and business owners, this course makes AI development accessible to everyone.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Rapid Implementation – Skip the long learning curve and go straight to building your app.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Support & Resources – Get templates, tools, and expert guidance to help you succeed.</span>
        </li>
      </ul>
      <p className="text-sm text-gray-700 mt-2 text-left">By the end of the weekend, you'll have a working AI-powered app and the confidence to refine, expand, and monetize your idea.</p>
    </div>
  ),
  'accelerator': (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 font-medium text-left">Monetize & Scale Your AI App Like a Pro</p>
      <p className="text-sm text-gray-700 text-left">Turn your AI-powered app into a profitable and scalable business with proven growth and monetization strategies. This comprehensive guide will teach you how to effectively launch, optimize, and expand your app while leveraging automation to maximize revenue with minimal effort.</p>
      <p className="font-medium text-sm text-left">What You'll Learn:</p>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">AI Monetization Models – Explore different ways to generate revenue, from subscriptions and in-app purchases to advertising and API licensing.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Advanced Automation Techniques – Learn how to streamline marketing, user acquisition, and customer support using AI-driven automation.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">User Growth & Retention Strategies – Discover powerful techniques to attract new users, increase engagement, and build long-term customer loyalty.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Scaling Without Limits – Implement growth hacks and performance optimization methods to expand your app without overwhelming your resources.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">AI-Driven Business Insights – Use data analytics and machine learning to refine your product, improve user experience, and increase profitability.</span>
        </li>
      </ul>
      <p className="font-medium text-sm mt-3 text-left">Why This Matters:</p>
      <ul className="space-y-1">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Unlock Passive Income – Set up automated revenue streams that generate income while you focus on scaling.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Outsmart the Competition – Use AI-powered tools to gain a competitive edge and stay ahead in the market.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Build a Sustainable Business – Learn how to continuously optimize your app for long-term growth and success.</span>
        </li>
      </ul>
    </div>
  ),
  'business-mastery': (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 font-medium text-left">Master AI App Development & Build a Profitable Business in 90 Days</p>
      <p className="text-sm text-gray-700 text-left">Take your AI app idea from concept to a thriving business with this in-depth, results-driven program. Designed for serious entrepreneurs, this 90-day masterclass provides a structured, step-by-step approach to AI-powered app development, automation, and monetization.</p>
      <p className="font-medium text-sm text-left">What You'll Learn:</p>
      
      <p className="text-sm font-medium text-blue-600 text-left">🚀 Phase 1: AI-Powered App Development</p>
      <ul className="space-y-1 mb-3">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">AI Fundamentals & App Development Basics – Understand how AI works, its applications in app development, and the best no-code/low-code platforms to get started.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Idea Validation & Market Research – Identify profitable app ideas, validate demand, and refine your concept based on market insights.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Hands-On App Building – Design and develop a functional AI-powered app, even if you have no coding experience.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">AI Automation & Smart Features – Integrate AI-driven features such as chatbots, automation, personalized recommendations, and data-driven decision-making.</span>
        </li>
      </ul>
      
      <p className="text-sm font-medium text-blue-600 text-left">📈 Phase 2: Launch & Monetization Strategies</p>
      <ul className="space-y-1 mb-3">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Profitable Monetization Models – Learn how to generate revenue with in-app purchases, subscriptions, AI-powered services, and advertising.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">User Acquisition & Growth Hacking – Implement proven strategies to attract your first 1,000 users and scale your audience efficiently.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Optimized App Store Launch – Get step-by-step guidance on launching your app on the Apple App Store and Google Play Store with maximum visibility.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">AI-Powered Marketing & Engagement – Leverage AI-driven marketing tools for automated customer acquisition, email campaigns, and user retention.</span>
        </li>
      </ul>
      
      <p className="text-sm font-medium text-blue-600 text-left">💰 Phase 3: Scaling & Business Automation</p>
      <ul className="space-y-1 mb-3">
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Growth & Expansion Strategies – Learn how to scale your app, attract investors, and turn your AI app into a sustainable business.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Automation & AI-Driven Business Models – Use AI to automate processes, optimize operations, and reduce manual effort.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Investor & Funding Strategies – Understand how to pitch your AI app to investors, secure funding, and position your business for long-term success.</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="h-4 w-4 text-green-500 mt-0.5" />
          <span className="text-sm text-left">Long-Term Success & Future Opportunities – Stay ahead of the curve with emerging AI trends and continuous innovation techniques.</span>
        </li>
      </ul>
    </div>
  ),
};

export { productDescriptions };
